// Copyright (c) 2026 GLOBAL MICRO IT TECHNOLOGY SOLUTIONS LIMITED
// Company No. 758762, Dublin, Ireland
// All rights reserved. Proprietary and confidential.

import { useState, useEffect, useCallback } from 'react';
import {
  Button,
  Dropdown,
  Option,
  Tooltip,
  type OptionOnSelectData,
  type SelectionEvents,
  Caption1,
} from '@fluentui/react-components';
import { PinRegular, PinOffRegular } from '@fluentui/react-icons';
import { useIntl } from 'react-intl';
import { useAuth } from '../auth/useAuth';
import { apiUrl } from '../utils/api-url';

interface TenantInfo {
  customerId: string;
  name: string;
}

interface TenantPickerProps {
  currentCustomerId: string;
  onTenantChange: (customerId: string) => void;
  rememberOrganisation: boolean;
  onToggleRemember: () => void;
}

/**
 * Header customer/tenant picker for MSP users. Loads available tenants from
 * GET /api/settings/tenants. Renders the empty-tenant caption when none are
 * onboarded — degrades gracefully if the endpoint is unavailable.
 *
 * Fetches with a bearer token from useAuth so it does not depend on any
 * per-agent api-client contract. Shared component — Shared-CSS-Reference v3.1 §2.1.
 */
export const TenantPicker = ({ currentCustomerId, onTenantChange, rememberOrganisation, onToggleRemember }: TenantPickerProps) => {
  const intl = useIntl();
  const { getAccessToken } = useAuth();
  const [tenants, setTenants] = useState<TenantInfo[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const loadTenants = async () => {
      try {
        const token = await getAccessToken();
        const res = await fetch(apiUrl('/api/settings/tenants'), {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.ok) {
          const data = await res.json() as { tenants?: TenantInfo[] };
          setTenants(data.tenants ?? []);
        }
      } catch {
        // Non-critical — the user can still use the default tenant.
      } finally {
        setIsLoading(false);
      }
    };
    void loadTenants();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-select first tenant when current selection is not in the list
  const stableOnTenantChange = useCallback(onTenantChange, [onTenantChange]);
  useEffect(() => {
    if (isLoading || tenants.length === 0) return;
    const currentInList = tenants.some(t => t.customerId === currentCustomerId);
    if (!currentInList) {
      stableOnTenantChange(tenants[0].customerId);
    }
  }, [isLoading, tenants, currentCustomerId, stableOnTenantChange]);

  const onSelect = (_event: SelectionEvents, data: OptionOnSelectData) => {
    const selectedId = data.optionValue;
    if (selectedId) {
      onTenantChange(selectedId);
    }
  };

  if (isLoading) {
    return null;
  }

  if (tenants.length === 0) {
    return (
      <div className="flex-row">
        <Caption1>{intl.formatMessage({ id: 'tenantPicker.label' })}</Caption1>
        <Caption1 className="text-muted">{intl.formatMessage({ id: 'tenantPicker.noTenants' })}</Caption1>
      </div>
    );
  }

  return (
    <div className="flex-row">
      <Caption1>{intl.formatMessage({ id: 'tenantPicker.label' })}</Caption1>
      <Dropdown
        size="small"
        value={tenants.find(t => t.customerId === currentCustomerId)?.name ?? currentCustomerId}
        selectedOptions={[currentCustomerId]}
        onOptionSelect={onSelect}
        style={{ minInlineSize: '200px' }}
      >
        {tenants.map(t => (
          <Option key={t.customerId} value={t.customerId}>
            {t.name}
          </Option>
        ))}
      </Dropdown>
      <Tooltip
        content={intl.formatMessage({ id: 'preferences.rememberOrg.description' })}
        relationship="description"
      >
        <Button
          size="small"
          appearance="subtle"
          icon={rememberOrganisation ? <PinRegular /> : <PinOffRegular />}
          onClick={onToggleRemember}
          aria-label={intl.formatMessage({ id: 'preferences.rememberOrg' })}
        />
      </Tooltip>
    </div>
  );
};
